/**
 * TMS — Module 8 Quản lý vận tải. GĐ1 Danh mục nền (v0.43, migration 042–043).
 * Kiểu dữ liệu, nhãn tiếng Việt, hàm thuần (có test) và truy cập dữ liệu.
 * Thiết kế: docs/tms/GD1_danh_muc_nen.md
 */
import { useEffect, useState } from 'react'
import { supabase } from '@/lib/supabase'

// =============================================================
// Kiểu
// =============================================================

export type TmsRole = 'khkd' | 'doi_xe' | 'bdsc' | 'kttc' | 'tchc' | 'bgd'

export const TMS_ROLE_LABEL: Record<TmsRole, string> = {
  khkd: 'Kế hoạch Kinh doanh',
  doi_xe: 'Đội xe',
  bdsc: 'Bảo dưỡng sửa chữa',
  kttc: 'Kế toán Tài chính',
  tchc: 'TCHC (quản trị TMS)',
  bgd: 'Ban Giám đốc',
}

export interface TmsRoleGrant { role: TmsRole | null; department_id: string | null; is_driver: boolean }

/** Quyền TMS của người đang đăng nhập (đã gộp admin_he_thong = mọi vai trò). */
export interface TmsAccess {
  isAdmin: boolean
  roles: { role: TmsRole; department_id: string | null }[]
  isDriver: boolean
}

export const CUSTOMER_TYPE_LABEL = {
  co_quan_bo: 'Cơ quan Bộ',
  co_quan_nha_nuoc: 'Cơ quan nhà nước',
  co_quan_dai_dien_nn: 'Cơ quan đại diện nước ngoài',
  to_chuc_quoc_te: 'Tổ chức quốc tế',
  doanh_nghiep: 'Doanh nghiệp',
  ca_nhan: 'Cá nhân',
  khac: 'Khác',
} as const

export const CUSTOMER_STATUS_LABEL = { active: 'Hoạt động', inactive: 'Ngừng' } as const
export const MISSION_STATUS_LABEL = { draft: 'Đang soạn', active: 'Đang thực hiện', closed: 'Đã đóng' } as const
export const CONTRACT_STATUS_LABEL = { draft: 'Đang soạn', active: 'Hiệu lực', expired: 'Hết hạn', terminated: 'Thanh lý' } as const
export const PRICE_LIST_STATUS_LABEL = { draft: 'Đang soạn', active: 'Hiệu lực', archived: 'Lưu trữ' } as const

export const PRICE_ITEM_LABEL = {
  trip: 'Theo chuyến',
  km: 'Theo km',
  day: 'Theo ngày',
  half_day: 'Nửa ngày',
  hour: 'Theo giờ',
  extra_km: 'Km vượt',
  overtime_hour: 'Giờ ngoài giờ',
  night_hour: 'Giờ đêm',
  waiting_hour: 'Giờ chờ',
  overnight: 'Lưu đêm',
  holiday: 'Ngày lễ',
  weekend: 'Cuối tuần',
} as const
export type PriceItemType = keyof typeof PRICE_ITEM_LABEL
/** Loại giá được phép tính theo % (khớp CHECK trong DB). */
export const PERCENT_ITEM_TYPES: PriceItemType[] = ['holiday', 'weekend', 'night_hour', 'overtime_hour']

export const LOCATION_TYPE_LABEL = {
  san_bay: 'Sân bay',
  ga: 'Ga / bến',
  khach_san: 'Khách sạn',
  co_quan: 'Cơ quan',
  dai_su_quan: 'Đại sứ quán',
  di_tich: 'Di tích / tham quan',
  benh_vien: 'Bệnh viện',
  khac: 'Khác',
} as const

export const FUEL_TYPE_LABEL = { xang: 'Xăng', dau: 'Dầu', dien: 'Điện', hybrid: 'Hybrid' } as const
export type FuelType = keyof typeof FUEL_TYPE_LABEL
export const OWNERSHIP_LABEL = { v75: 'V75', thue_ngoai: 'Thuê ngoài' } as const
export type VehicleOwnership = keyof typeof OWNERSHIP_LABEL
export const VEHICLE_STATUS_LABEL = { dang_su_dung: 'Đang sử dụng', tam_ngung: 'Tạm ngừng', thanh_ly: 'Thanh lý' } as const
export const VENDOR_STATUS_LABEL = { active: 'Hợp tác', suspended: 'Tạm dừng', terminated: 'Chấm dứt' } as const
export const DRIVER_SOURCE_LABEL = { v75: 'Lái V75', nha_xe: 'Lái nhà xe' } as const
export type TmsDriverSource = keyof typeof DRIVER_SOURCE_LABEL
export const DRIVER_STATUS_LABEL = { dang_lai: 'Đang lái', tam_dung: 'Tạm dừng', nghi: 'Nghỉ' } as const
export type DriverStatus = keyof typeof DRIVER_STATUS_LABEL

/** Dòng dữ liệu chung của mọi bảng danh mục (đọc qua PostgREST). */
export type Row = Record<string, unknown> & { id: string }

export interface Option { value: string; label: string }

/** Option từ object nhãn { value: label }. */
export const labelOptions = (m: Record<string, string>): Option[] =>
  Object.entries(m).map(([value, label]) => ({ value, label }))

/** Option từ danh sách dòng (mã – tên). */
export const rowOptions = (rows: Row[], label: (r: Row) => string = r => `${String(r.code)} – ${String(r.name ?? r.full_name ?? '')}`): Option[] =>
  rows.map(r => ({ value: r.id, label: label(r) }))

export interface LicenseClass {
  id: string; code: string; name: string; regime: 'luat_2024' | 'truoc_2025'
  max_passengers: number | null; description: string | null; sort_order: number
}
export interface VehicleType {
  id: string; code: string; name: string; seat_count: number
  required_license_class_id: string; toll_class: number; sort_order: number; note: string | null
}
export interface FleetTeam { id: string; code: string; name: string }

export interface PriceList {
  id: string; code: string; name: string; contract_id: string | null
  effective_from: string; effective_to: string | null
  status: keyof typeof PRICE_LIST_STATUS_LABEL
  approved_at: string | null; note: string | null
}
export interface PriceListItem {
  id: string; price_list_id: string; vehicle_type_id: string | null; route_id: string | null
  item_type: PriceItemType; amount: number | null; percent: number | null
  included_km: number | null; included_hours: number | null; note: string | null
}

export interface FleetEmployee {
  employee_id: string; ho_ten: string; so_dien_thoai: string | null
  dept_code: string; dept_name: string; has_driver: boolean
}
export interface DirectoryUser {
  user_id: string; display_name: string; dept_code: string | null; dept_name: string | null; role: string
}
export interface TmsUserRoleRow {
  id: string; user_id: string; role: TmsRole; department_id: string | null; note: string | null
}

// =============================================================
// Hàm thuần
// =============================================================

/** Chuẩn hoá biển số giống cột sinh `plate_normalized` trong DB. */
export function normalizePlate(plate: string): string {
  return plate.replace(/[^0-9A-Za-z]/g, '').toUpperCase()
}

/** Biển số dân sự VN sau chuẩn hoá: 29A12345, 30LD12345, 29A112345. */
export function isValidPlate(plate: string): boolean {
  return /^[0-9]{2}[A-Z]{1,2}[0-9]?[0-9]{4,5}$/.test(normalizePlate(plate))
}

/** Mức cảnh báo hạn (bằng lái, khám sức khoẻ): theo tham số doc_warning_days. */
export type ExpiryLevel = 'missing' | 'expired' | 'urgent' | 'soon' | 'warn' | 'ok'

export function expiryLevel(expiry: string | null | undefined, today: Date, warnDays: number[] = [30, 15, 7]): ExpiryLevel {
  if (!expiry) return 'missing'
  const [y, m, d] = expiry.split('-').map(Number)
  const end = Date.UTC(y, m - 1, d)
  const now = Date.UTC(today.getFullYear(), today.getMonth(), today.getDate())
  const days = Math.round((end - now) / 86_400_000)
  if (days < 0) return 'expired'
  const [w1, w2, w3] = [...warnDays].sort((a, b) => b - a)
  if (w3 !== undefined && days <= w3) return 'urgent'
  if (w2 !== undefined && days <= w2) return 'soon'
  if (w1 !== undefined && days <= w1) return 'warn'
  return 'ok'
}

/** Mã kế tiếp theo tiền tố (VD LX-007 → LX-008), giữ độ dài số. */
export function nextCode(existing: string[], prefix: string, width = 3): string {
  const re = new RegExp(`^${prefix.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&')}(\\d+)$`)
  const max = existing.reduce((m, c) => {
    const hit = re.exec(c)
    return hit ? Math.max(m, Number(hit[1])) : m
  }, 0)
  return prefix + String(max + 1).padStart(width, '0')
}

/** Loại xe mà hạng bằng `classId` lái được (theo bảng license_class_coverage). */
export function drivableVehicleTypes(
  classId: string | null | undefined,
  coverage: { holder_class_id: string; covered_class_id: string }[],
  types: VehicleType[],
): VehicleType[] {
  if (!classId) return []
  const covered = new Set(coverage.filter(c => c.holder_class_id === classId).map(c => c.covered_class_id))
  return types.filter(t => covered.has(t.required_license_class_id))
}

/** Gộp quyền: admin hệ thống có mọi vai trò; doi_xe có thể giới hạn theo đội. */
export function hasTmsRole(access: TmsAccess | null, role: TmsRole, deptId?: string | null): boolean {
  if (!access) return false
  if (access.isAdmin) return true
  return access.roles.some(r => r.role === role && (!deptId || !r.department_id || r.department_id === deptId))
}

export function isTmsStaff(access: TmsAccess | null): boolean {
  return !!access && (access.isAdmin || access.roles.length > 0)
}

/** Thông báo lỗi DB → câu dễ hiểu cho người dùng. */
export function tmsErrorText(e: unknown, fallback: string): string {
  const err = e as { code?: string; message?: string } | null
  const msg = err?.message ?? ''
  if (err?.code === '23505') {
    if (msg.includes('plate')) return 'Biển số này đã có trong danh mục.'
    if (msg.includes('tax')) return 'Mã số thuế này đã có.'
    if (msg.includes('license')) return 'Số bằng lái này đã có.'
    if (msg.includes('employee_id')) return 'Nhân viên này đã có hồ sơ lái xe.'
    if (msg.includes('price_items')) return 'Đã có dòng giá cùng loại xe, tuyến và loại giá.'
    return 'Mã hoặc dữ liệu bị trùng với bản ghi đã có.'
  }
  if (err?.code === '23514' || msg.includes('check constraint')) {
    if (msg.includes('ck_vehicles_plate')) return 'Biển số không đúng mẫu (VD 29A-123.45).'
    if (msg.includes('ck_vehicles_owner')) return 'Xe V75 phải chọn đội; xe thuê ngoài phải chọn nhà xe.'
    if (msg.includes('ck_vehicles_capacity')) return 'Xe V75 phải khai dung tích bình (xăng/dầu) hoặc dung lượng pin (điện).'
    if (msg.includes('ck_drivers_source')) return 'Lái V75 phải chọn nhân viên; lái nhà xe phải chọn nhà xe.'
    if (msg.includes('tax_code')) return 'Mã số thuế phải gồm 10 số (hoặc 10 số - 3 số).'
  }
  if (err?.code === '42501' && msg.includes('row-level security')) return 'Bạn không có quyền thực hiện thao tác này.'
  if (err?.code === '23P01') return 'Ngày hiệu lực chồng lên bảng giá khác đang hiệu lực.'
  return msg || fallback
}

// =============================================================
// Truy cập dữ liệu
// =============================================================

export type CatalogTable =
  | 'customers' | 'missions' | 'service_contracts' | 'locations' | 'toll_stations'
  | 'vendors' | 'vehicles' | 'drivers' | 'routes' | 'vehicle_types' | 'maintenance_levels'

/** Danh sách chưa xoá mềm. */
export async function listRows(table: CatalogTable, order = 'code'): Promise<Row[]> {
  const { data, error } = await supabase.from(table).select('*').is('deleted_at', null).order(order)
  if (error) throw error
  return (data ?? []) as Row[]
}

export async function saveRow(table: CatalogTable, id: string | undefined, payload: Record<string, unknown>): Promise<Row> {
  const q = id
    ? supabase.from(table).update(payload).eq('id', id)
    : supabase.from(table).insert(payload)
  const { data, error } = await q.select().single()
  if (error) throw error
  return data as Row
}

export async function softDeleteRow(table: CatalogTable, id: string): Promise<void> {
  const { error } = await supabase.from(table).update({ deleted_at: new Date().toISOString() }).eq('id', id)
  if (error) throw error
}

export async function fetchTmsAccess(isAdmin: boolean): Promise<TmsAccess> {
  const { data, error } = await supabase.rpc('tms_my_roles')
  if (error) throw error
  const rows = (data ?? []) as TmsRoleGrant[]
  return {
    isAdmin,
    roles: rows.filter(r => r.role).map(r => ({ role: r.role as TmsRole, department_id: r.department_id })),
    isDriver: rows.some(r => r.is_driver),
  }
}

// Cache trong phiên để Sidebar + trang không gọi lặp
let accessCache: { key: string; value: Promise<TmsAccess> } | null = null

/** Quyền TMS của tài khoản hiện tại (null khi đang tải / lỗi). */
export function useTmsAccess(userId: string | undefined, isAdmin: boolean): TmsAccess | null {
  const [access, setAccess] = useState<TmsAccess | null>(null)
  useEffect(() => {
    if (!userId) { setAccess(null); return }
    const key = `${userId}:${isAdmin}`
    if (!accessCache || accessCache.key !== key) {
      accessCache = { key, value: fetchTmsAccess(isAdmin) }
      accessCache.value.catch(() => { accessCache = null })
    }
    let alive = true
    accessCache.value
      .then(a => { if (alive) setAccess(a) })
      .catch(() => { if (alive) setAccess({ isAdmin, roles: [], isDriver: false }) })
    return () => { alive = false }
  }, [userId, isAdmin])
  return access
}

/** Xoá cache khi TCHC vừa sửa vai trò (để menu/trang cập nhật). */
export function invalidateTmsAccess(): void { accessCache = null }

export async function listFleetTeams(): Promise<FleetTeam[]> {
  const { data, error } = await supabase.from('departments')
    .select('id, code, name').eq('is_fleet_team', true).order('code')
  if (error) throw error
  return (data ?? []) as FleetTeam[]
}

export async function listLicenseClasses(): Promise<LicenseClass[]> {
  const { data, error } = await supabase.from('license_classes').select('*').order('sort_order')
  if (error) throw error
  return (data ?? []) as LicenseClass[]
}

export async function listLicenseCoverage(): Promise<{ holder_class_id: string; covered_class_id: string }[]> {
  const { data, error } = await supabase.from('license_class_coverage').select('holder_class_id, covered_class_id')
  if (error) throw error
  return data ?? []
}

export async function listVehicleTypes(): Promise<VehicleType[]> {
  const { data, error } = await supabase.from('vehicle_types').select('*').is('deleted_at', null).order('sort_order')
  if (error) throw error
  return (data ?? []) as VehicleType[]
}

export async function getWarnDays(): Promise<number[]> {
  const { data } = await supabase.from('tms_settings').select('value').eq('key', 'doc_warning_days').maybeSingle()
  const v = (data as { value?: unknown } | null)?.value
  return Array.isArray(v) && v.every(n => typeof n === 'number') ? v as number[] : [30, 15, 7]
}

export async function listTmsSettings(): Promise<{ id: string; key: string; value: unknown; description: string | null }[]> {
  const { data, error } = await supabase.from('tms_settings').select('id, key, value, description').order('key')
  if (error) throw error
  return data ?? []
}

export async function updateTmsSetting(id: string, value: unknown): Promise<void> {
  const { error } = await supabase.from('tms_settings').update({ value }).eq('id', id)
  if (error) throw error
}

// --- Tuyến: điểm dừng + trạm thu phí --------------------------
export async function getRouteDetail(routeId: string): Promise<{ stops: string[]; tolls: string[] }> {
  const [s, t] = await Promise.all([
    supabase.from('route_stops').select('location_id, seq').eq('route_id', routeId).order('seq'),
    supabase.from('route_toll_stations').select('toll_station_id, seq').eq('route_id', routeId).order('seq'),
  ])
  if (s.error) throw s.error
  if (t.error) throw t.error
  return {
    stops: (s.data ?? []).map(r => r.location_id as string),
    tolls: (t.data ?? []).map(r => r.toll_station_id as string),
  }
}

export async function saveRouteDetail(routeId: string, stops: string[], tolls: string[]): Promise<void> {
  const d1 = await supabase.from('route_stops').delete().eq('route_id', routeId)
  if (d1.error) throw d1.error
  const d2 = await supabase.from('route_toll_stations').delete().eq('route_id', routeId)
  if (d2.error) throw d2.error
  if (stops.length) {
    const { error } = await supabase.from('route_stops')
      .insert(stops.map((location_id, i) => ({ route_id: routeId, seq: i + 1, location_id })))
    if (error) throw error
  }
  if (tolls.length) {
    const { error } = await supabase.from('route_toll_stations')
      .insert(tolls.map((toll_station_id, i) => ({ route_id: routeId, seq: i + 1, toll_station_id })))
    if (error) throw error
  }
}

// --- Phí trạm thu phí ------------------------------------------
export interface TollFee { id: string; toll_station_id: string; toll_class: number; fee: number; effective_from: string }

export async function listTollFees(stationId: string): Promise<TollFee[]> {
  const { data, error } = await supabase.from('toll_station_fees').select('*')
    .eq('toll_station_id', stationId).order('effective_from', { ascending: false }).order('toll_class')
  if (error) throw error
  return (data ?? []) as TollFee[]
}

export async function addTollFee(f: Omit<TollFee, 'id'>): Promise<void> {
  const { error } = await supabase.from('toll_station_fees').insert(f)
  if (error) throw error
}

export async function deleteTollFee(id: string): Promise<void> {
  const { error } = await supabase.from('toll_station_fees').delete().eq('id', id)
  if (error) throw error
}

// --- Bảng giá ----------------------------------------------------
export async function listPriceLists(): Promise<PriceList[]> {
  const { data, error } = await supabase.from('price_lists').select('*').is('deleted_at', null)
    .order('effective_from', { ascending: false })
  if (error) throw error
  return (data ?? []) as PriceList[]
}

export async function savePriceList(id: string | undefined, p: Partial<PriceList>): Promise<PriceList> {
  const q = id ? supabase.from('price_lists').update(p).eq('id', id) : supabase.from('price_lists').insert(p)
  const { data, error } = await q.select().single()
  if (error) throw error
  return data as PriceList
}

export async function softDeletePriceList(id: string): Promise<void> {
  const { error } = await supabase.from('price_lists').update({ deleted_at: new Date().toISOString() }).eq('id', id)
  if (error) throw error
}

export async function listPriceItems(priceListId: string): Promise<PriceListItem[]> {
  const { data, error } = await supabase.from('price_list_items').select('*').eq('price_list_id', priceListId)
  if (error) throw error
  return (data ?? []) as PriceListItem[]
}

export async function savePriceItem(id: string | undefined, p: Partial<PriceListItem>): Promise<void> {
  const { error } = id
    ? await supabase.from('price_list_items').update(p).eq('id', id)
    : await supabase.from('price_list_items').insert(p)
  if (error) throw error
}

export async function deletePriceItem(id: string): Promise<void> {
  const { error } = await supabase.from('price_list_items').delete().eq('id', id)
  if (error) throw error
}

/** Sao chép bảng giá thành phiên bản mới (Đang soạn) kèm toàn bộ đơn giá. */
export async function clonePriceList(src: PriceList, code: string, effectiveFrom: string): Promise<PriceList> {
  const created = await savePriceList(undefined, {
    code, name: src.name, contract_id: src.contract_id, effective_from: effectiveFrom, note: src.note,
  })
  const items = await listPriceItems(src.id)
  if (items.length) {
    const { error } = await supabase.from('price_list_items').insert(items.map(i => ({
      price_list_id: created.id, vehicle_type_id: i.vehicle_type_id, route_id: i.route_id,
      item_type: i.item_type, amount: i.amount, percent: i.percent,
      included_km: i.included_km, included_hours: i.included_hours, note: i.note,
    })))
    if (error) throw error
  }
  return created
}

export async function activatePriceList(id: string): Promise<void> {
  const { error } = await supabase.rpc('tms_price_list_activate', { p_id: id })
  if (error) throw error
}

export async function archivePriceList(id: string): Promise<void> {
  const { error } = await supabase.rpc('tms_price_list_archive', { p_id: id })
  if (error) throw error
}

// --- Xe, lái xe ----------------------------------------------------
export async function changeVehicleTeam(vehicleId: string, teamId: string, reason: string): Promise<void> {
  const { error } = await supabase.rpc('tms_vehicle_change_team', { p_vehicle: vehicleId, p_team: teamId, p_reason: reason })
  if (error) throw error
}

export async function listFleetEmployees(): Promise<FleetEmployee[]> {
  const { data, error } = await supabase.rpc('tms_fleet_employees')
  if (error) throw error
  return (data ?? []) as FleetEmployee[]
}

export async function bulkCreateDrivers(): Promise<number> {
  const { data, error } = await supabase.rpc('tms_drivers_bulk_from_employees')
  if (error) throw error
  return Number(data ?? 0)
}

/** Upsert hàng loạt theo `code` (nhập Excel) — trả lỗi từng dòng, không dừng cả lô. */
export async function upsertByCode(
  table: 'vehicles' | 'drivers', rows: Record<string, unknown>[],
): Promise<{ ok: number; errors: { code: string; message: string }[] }> {
  let ok = 0
  const errors: { code: string; message: string }[] = []
  for (const r of rows) {
    const code = String(r.code)
    const { data: found, error: e1 } = await supabase.from(table).select('id').eq('code', code).maybeSingle()
    if (e1) { errors.push({ code, message: tmsErrorText(e1, 'Lỗi đọc dữ liệu') }); continue }
    const { error } = found
      ? await supabase.from(table).update(r).eq('id', (found as { id: string }).id)
      : await supabase.from(table).insert(r)
    if (error) errors.push({ code, message: tmsErrorText(error, 'Lỗi lưu') })
    else ok++
  }
  return { ok, errors }
}

/** Chỉ CẬP NHẬT theo `code` (nhập bằng lái) — mã không có thì báo lỗi dòng đó. */
export async function updateByCode(
  table: 'drivers', rows: Record<string, unknown>[],
): Promise<{ ok: number; errors: { code: string; message: string }[] }> {
  let ok = 0
  const errors: { code: string; message: string }[] = []
  for (const { code, ...rest } of rows) {
    const { data, error } = await supabase.from(table).update(rest).eq('code', String(code)).is('deleted_at', null).select('id')
    if (error) errors.push({ code: String(code), message: tmsErrorText(error, 'Lỗi lưu') })
    else if (!data?.length) errors.push({ code: String(code), message: 'Không có mã lái xe này hoặc bạn không có quyền sửa' })
    else ok++
  }
  return { ok, errors }
}

// --- Phân quyền TMS ------------------------------------------------
export async function listTmsUserRoles(): Promise<TmsUserRoleRow[]> {
  const { data, error } = await supabase.from('tms_user_roles').select('id, user_id, role, department_id, note')
    .is('deleted_at', null)
  if (error) throw error
  return (data ?? []) as TmsUserRoleRow[]
}

export async function grantTmsRole(userId: string, role: TmsRole, departmentId: string | null): Promise<void> {
  const { error } = await supabase.from('tms_user_roles').insert({ user_id: userId, role, department_id: departmentId })
  if (error) throw error
  invalidateTmsAccess()
}

export async function revokeTmsRole(id: string): Promise<void> {
  const { error } = await supabase.from('tms_user_roles').update({ deleted_at: new Date().toISOString() }).eq('id', id)
  if (error) throw error
  invalidateTmsAccess()
}

export async function listUserDirectory(): Promise<DirectoryUser[]> {
  const { data, error } = await supabase.rpc('tms_user_directory')
  if (error) throw error
  return (data ?? []) as DirectoryUser[]
}
